import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { supabase } from "../lib/supabase";
import { Avatar } from "../components/Avatar";
import { memberColor } from "../shared/reveal-card";
import { BlobField } from "../components/BlobField";
import { CaughtUp } from "../components/CaughtUp";
import { WhatsNext } from "../components/WhatsNext";
import { buzz } from "../lib/haptics";
import { rankBadge } from "../shared/format";
import { seasonSuperlatives, type Superlative } from "../shared/superlatives";
import {
  getLeagueState, getLeaderboardWithMovement, getRecentReveals, getSubjectStats, type Member,
} from "../lib/league";

// The season finale.
//
// Read-only and entirely additive: it renders existing data and touches no game
// logic, no scoring, no scheduling. It is gated on the season being complete so
// it cannot appear mid-season, with a commissioner-only preview so the host can
// see it before the league does.

type Beat = "champion" | "standings" | "superlatives" | "recap" | "close";

export function Finale() {
  const { leagueId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [failed, setFailed] = useState(false);
  const [beat, setBeat] = useState<Beat>("champion");
  // Lifted so the footer button can step back while the deck is still going.
  const [recapDone, setRecapDone] = useState(false);

  async function load() {
    setFailed(false);
    try {
      const { league, season, members, me } = await getLeagueState(leagueId!, user!.id);
      if (!season) { setData({ noSeason: true, league, me }); return; }
      const [board, updates, subjects] = await Promise.all([
        getLeaderboardWithMovement(season.id, members as Member[]),
        // The whole season, not the handful Home shows.
        getRecentReveals(season.id, 500),
        getSubjectStats(season.id),
      ]);
      const nameOf = (id: string) =>
        (members as Member[]).find((m) => m.user_id === id)?.display_name || "Friend";
      // The host's tally and the real season count come from the server, which
      // is the only thing that can count votes without showing whose they are.
      let tally, seasonsPlayed;
      try {
        const st = await api<any>(`/api/league/admin`, { body: { op: "status", leagueId } });
        tally = st?.tally;
        seasonsPlayed = st?.seasonsPlayed;
      } catch { /* members cannot call status, and do not need it */ }

      // Your own vote is readable directly: the policy exposes yours and nobody
      // else's.
      const { data: mine } = await supabase
        .from("season_votes").select("choice")
        .eq("season_id", season.id).eq("user_id", user!.id).maybeSingle();

      setData({
        league, season, members, me, board, updates, nameOf,
        superlatives: seasonSuperlatives(subjects, board, nameOf),
        tally, seasonsPlayed, myVote: mine?.choice ?? null,
      });
    } catch {
      setFailed(true);
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [leagueId, user]);

  if (failed) {
    return (
      <div className="app" data-surface="light">
        <div className="header">
          <h1>Season finale</h1>
          <button className="link" onClick={() => navigate(`/league/${leagueId}`)}>Home</button>
        </div>
        <div className="card">
          <p className="error">We couldn&apos;t load the finale.</p>
          <p className="muted">That is usually the connection. Try again.</p>
          <button className="btn primary" onClick={load}>Try again</button>
        </div>
      </div>
    );
  }
  if (!data) return <div className="app" data-surface="light"><div className="loading">Putting the season together…</div></div>;

  const { league, season, me, board, updates, nameOf, superlatives } = data;
  const isComplete = season?.status === "complete";
  const isHost = me?.role === "commissioner";

  // Members must never see this before the season actually ends.
  if (!isComplete && !isHost) {
    return (
      <div className="app" data-surface="light">
        <div className="header">
          <h1>Not yet</h1>
          <button className="link" onClick={() => navigate(`/league/${leagueId}`)}>Home</button>
        </div>
        <div className="card live">
          <p>The season is still running. This is where it all gets wrapped up when it&apos;s done.</p>
          <button className="btn primary" onClick={() => navigate(`/league/${leagueId}`)}>Back to the crew</button>
        </div>
      </div>
    );
  }

  const champion = board?.length && board[0].points > 0 ? board[0] : null;
  const withUpdates = (updates ?? []).filter((u: any) => (u.reveal_context ?? "").trim());
  const order: Beat[] = ["champion", "standings"];
  if (superlatives.length > 0) order.push("superlatives");
  if (withUpdates.length > 0) order.push("recap");
  order.push("close");
  const at = Math.max(0, order.indexOf(beat));
  const next = order[at + 1];

  return (
    <div className="app finale" data-surface="light">
      <div className="header">
        <h1>{league?.name ?? "Season"}</h1>
        <button className="link" onClick={() => navigate(`/league/${leagueId}`)}>Home</button>
      </div>

      {!isComplete && (
        <div className="banner">
          <span>Preview. Only you can see this, and only until the season ends.</span>
        </div>
      )}

      <div className="beat-dots" aria-label={`Part ${at + 1} of ${order.length}`}>
        {order.map((b, i) => (
          <span key={b} className={`pip ${i < at ? "done" : i === at ? "current" : ""}`} />
        ))}
      </div>

      {beat === "champion" && <ChampionBeat league={league} champion={champion} youAre={user!.id} />}
      {beat === "standings" && <StandingsBeat board={board} youAre={user!.id} />}
      {beat === "superlatives" && <SuperlativesBeat items={superlatives} />}
      {beat === "recap" && (
        <>
          <div className="card">
            <span className="tag">The season in one place</span>
            <p className="muted">
              Everything the crew shared. Swipe through it, or jump straight to the list.
            </p>
          </div>
          <CaughtUp
            items={withUpdates}
            nameOf={nameOf}
            onFinished={() => setRecapDone(true)}
          />
        </>
      )}
      {beat === "close" && (
        <WhatsNext
          leagueId={leagueId!}
          crewName={league?.name ?? "the crew"}
          championName={champion?.name}
          isHost={isHost}
          tally={data.tally}
          seasonsPlayed={data.seasonsPlayed}
          myVote={data.myVote}
          onHome={() => navigate(`/league/${leagueId}`)}
        />
      )}

      {next && (
        // Pinned to the bottom of the screen rather than sitting wherever the
        // beat happens to end, so the way forward is always in the same place.
        <div className="finale-foot">
          {/* While the recap deck still has cards, the footer must not compete
              with "Next". A primary Finish under an unfinished deck invites a
              tap straight past the part the whole finale is built around. */}
          {beat === "recap" && !recapDone ? (
            <button className="btn ghost" onClick={() => setBeat(next)}>Skip to the end</button>
          ) : (
            <button className="btn primary" onClick={() => setBeat(next)}>
              {next === "close" ? "Finish" : "Next"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function ChampionBeat({ league, champion, youAre }: { league: any; champion: any; youAre: string }) {
  // The arrival beat. The screen blooms dark to light on its own; the buzz is
  // what makes it land in the hand as well as the eye.
  useEffect(() => { buzz("landing"); }, []);

  return (
    <div className="bloom champion-beat">
      <p className="finale-kicker">That&apos;s a wrap on {league?.name ?? "the season"}</p>
      {champion ? (
        <div className="card champion">
          {/* The same morphing field the rest of the app sits on, just livelier.
              The bespoke version here rose and sank on a fixed path and read as
              a glitch rather than motion. */}
          <div className="champ-blobs" aria-hidden="true">
            <BlobField count={7} speed={1.6} />
          </div>
          <span className="champ-shimmer" aria-hidden="true" />

          <p className="crown-label">Champion</p>
          <div className="champ-face">
            {/* A real photo is what would land this. Uploads are not in this
                version, so the member's own colour stands in and pulses. */}
            <span className="champ-pulse" aria-hidden="true" style={{ background: memberColor(champion.user_id) }} />
            <Avatar id={champion.user_id} name={champion.name} size={104} />
          </div>
          <h2 className="champion-name">{champion.name}</h2>
          <p className="champ-score">{champion.points}</p>
          <p className="muted">
            {champion.correct_count} right out of {champion.attempted_count}
            {champion.user_id === youAre ? " · that's you" : ""}
          </p>
        </div>
      ) : (
        <div className="card">
          <p className="crown-label">No crown this time</p>
          <p>
            Nobody put up points this season, so there is no champion to crown. Everything else the
            crew shared is still here.
          </p>
        </div>
      )}
    </div>
  );
}


function StandingsBeat({ board, youAre }: { board: any[]; youAre: string }) {
  const rows = board ?? [];
  // Only a real top three gets a podium. A crew of two gets the list, because a
  // podium with a hole in it looks like something failed to load.
  const podium = rows.length >= 3 && rows[0]?.points > 0 ? rows.slice(0, 3) : [];
  const rest = podium.length ? rows.slice(3) : rows;
  // Second, first, third: the shape everybody already knows.
  const arranged = podium.length ? [podium[1], podium[0], podium[2]] : [];

  return (
    <div className="card">
      <span className="tag">Final standings</span>

      {podium.length > 0 && (
        <div className="podium">
          {arranged.map((e: any) => (
            <div className={`podium-step place-${e.rank}`} key={e.user_id}>
              <Avatar id={e.user_id} name={e.name} size={e.rank === 1 ? 62 : 48} />
              <span className="podium-name">{e.name}{e.user_id === youAre ? " (you)" : ""}</span>
              <span className="podium-block">
                <span className="podium-rank">{e.rank}</span>
                <span className="podium-points">{e.points}</span>
                {e.attempted_count > 0 && (
                  <span className="podium-record">{e.correct_count}/{e.attempted_count}</span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}

      {rest.map((e: any) => (
        <div className={`lb-row ${e.user_id === youAre ? "me" : ""}`} key={e.user_id}>
          <span className="lb-rank">{rankBadge(e.rank, e.points)}</span>
          <Avatar id={e.user_id} name={e.name} size={30} />
          <span className="lb-name">
            {e.name}{e.user_id === youAre ? " (you)" : ""}
            {e.attempted_count > 0 && (
              <>
                <br />
                <span className="muted lb-record">{e.correct_count}/{e.attempted_count} correct</span>
              </>
            )}
          </span>
          <span className="lb-points">{e.points}</span>
        </div>
      ))}

      {rows.length === 0 && (
        <p className="muted">Nobody played a drop this season, so there is nothing to rank.</p>
      )}
      {rows.length > 0 && <p className="muted">1 point per correct answer. No speed bonus.</p>}
    </div>
  );
}

function SuperlativesBeat({ items }: { items: Superlative[] }) {
  return (
    <div className="card">
      <span className="tag">Season superlatives</span>
      {items.map((h) => (
        <div className="highlight" key={h.label}>
          <span className="highlight-label">{h.label}</span>
          <span className="highlight-name">{h.name}</span>
          <span className="muted highlight-detail">{h.detail}</span>
        </div>
      ))}
    </div>
  );
}

// The recap. A swipeable stack of everything the league shared this season,
// followed by the same list in full so nobody has to swipe to read it all.
